import { Navigate, useParams, Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { PageHero, useTitle } from "../components/PageHero";
import { Reveal, Tag } from "../components/Reveal";
import { PrimaryBtn, GhostBtn } from "../components/ui";
import { CTABand } from "../components/CTABand";
import { PRODUCTS, PRODUCT_CATEGORIES } from "../data/products";
import { waLink } from "../data/site";

export default function ProductDetail() {
  const { slug } = useParams();
  const p = PRODUCTS.find((x) => x.slug === slug);
  useTitle(p ? `${p.name} | SABAKT Saudi Arabia` : "SABAKT Products");
  if (!p) return <Navigate to="/products" replace />;

  const cat = PRODUCT_CATEGORIES.find((c) => c.slug === p.category);
  const related = PRODUCTS.filter((x) => x.category === p.category && x.slug !== slug).slice(0, 4);
  const gallery = p.gallery && p.gallery.length > 1 ? p.gallery : null;

  return (
    <main>
      <PageHero image={p.image} eyebrow={`Product · ${cat ? cat.name : "SABAKT"}`} testid="product-detail-hero" title={p.name} accent={p.name.split(" ").slice(-1)[0]}>
        <div className="mt-8 flex flex-wrap gap-4">
          <PrimaryBtn to="/rfq" testid="product-rfq-btn">Request Quotation</PrimaryBtn>
          <GhostBtn href={waLink(`Hello SABAKT, I'm interested in: ${p.name}`)} testid="product-whatsapp-btn">WhatsApp an Engineer</GhostBtn>
        </div>
      </PageHero>

      <section className="border-b border-seam py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-8 lg:grid-cols-[1.2fr_1fr] lg:px-16">
          <Reveal>
            <Tag>Overview</Tag>
            <p className="mt-4 text-lg leading-relaxed text-frost/90">{p.overview}</p>
            {p.brands && (
              <div className="mt-8 border border-seam bg-iron p-6" data-testid="product-brands">
                <Tag>Brand Compatibility</Tag>
                <p className="mt-3 font-display text-xl font-bold uppercase tracking-wide text-frost">{p.brands}</p>
                <p className="mt-2 text-sm text-cadmium">Replacement parts manufactured compatible with leading international mixer brands — verified against OEM samples or site measurements.</p>
              </div>
            )}
            <div className="mt-8">
              <Tag>Technical Data</Tag>
              <p className="mt-3 text-sm italic leading-relaxed text-cadmium/80">{p.formats}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-seam bg-iron">
              <div className="border-b border-seam px-7 py-4"><Tag>Applications</Tag></div>
              <ul className="divide-y divide-seam/60">
                {p.applications.map((a) => (
                  <li key={a} className="flex items-start gap-3 px-7 py-4 text-sm text-frost/85">
                    <Check size={16} className="mt-0.5 shrink-0 text-spark" /> {a}
                  </li>
                ))}
              </ul>
              <div className="border-b border-t border-seam px-7 py-4"><Tag>Key Benefits</Tag></div>
              <ul className="divide-y divide-seam/60">
                {p.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 px-7 py-4 text-sm text-frost/85">
                    <Check size={16} className="mt-0.5 shrink-0 text-gold" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {gallery && (
        <section className="border-b border-seam bg-iron py-20" data-testid="product-gallery">
          <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
            <Reveal>
              <Tag>From the SABAKT Workshop</Tag>
              <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-frost">Actual Product Photographs</h2>
            </Reveal>
            <div className="mt-10 grid gap-px border border-seam bg-seam sm:grid-cols-2 lg:grid-cols-4">
              {gallery.map((g, i) => (
                <Reveal key={g} delay={i * 0.06}>
                  <div className="group aspect-[4/3] overflow-hidden bg-obsidian">
                    <img src={g} alt={`${p.name} — photo ${i + 1}`} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="border-b border-seam py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
            <Tag>Related Products{cat ? ` · ${cat.name}` : ""}</Tag>
            <div className="mt-6 grid gap-px border border-seam bg-seam sm:grid-cols-2 lg:grid-cols-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/products/${r.slug}`} data-testid={`related-product-${r.slug}`}
                  className="group bg-obsidian transition-colors hover:bg-steel">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={r.image} alt={r.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex items-center justify-between gap-2 p-5">
                    <span className="font-display text-base font-bold uppercase tracking-wide text-frost/90 group-hover:text-spark">{r.name}</span>
                    <ArrowUpRight size={16} className="shrink-0 text-cadmium group-hover:text-spark" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand title={`Need ${p.name}?`} text="Send a drawing, a photo of the worn part, or an OEM part number — we'll verify dimensions and quote from our Riyadh workshop." />
    </main>
  );
}
